import React, { FC } from "react";
import "./App.css";
import {
  AckPage,
  AckSettings,
  AckStatusz,
  PrivateAckResource,
  useAuth,
} from "ackbox-weblib-react";
import {
  HashRouter as Router,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { Navigation } from "./app/Navigation";
import { RESOURCES_API_PATH } from "./app/Constants";
import { OverallSummary } from "./app/OverallSummary";
import { FrontPage } from "./app/FrontPage";
import { MonthlySummary } from "./app/MonthlySummary";
import { TransactionsImport } from "./app/TransactionsImport";
import { YearlySummary } from "./app/YearlySummary";
import { Simulations } from "./app/Simulations";
import {
  SecuritiesAcbBySecuritySummary,
  SecuritiesAcbByYearSummary,
  SecuritiesHoldingsSummary,
} from "./app/Securities";
import { Changelogs } from "./app/Changelogs";
import { Settings } from "./app/Settings";

export const App = () => {
  const settings: AckSettings = {
    sitename: "Xpenser",
    navigation: Navigation,
    authAudience: "https://xpenser.ackbx.com",
    authScope: "profile",
  };

  const CountableResource: FC<{ name: string }> = (props) => {
    const location = useLocation();
    return (
      <PrivateAckResource
        returnTo={location.pathname}
        apiPath={RESOURCES_API_PATH}
        name={props.name}
        listOptions={{
          groupFieldLabel: "Created At",
          groupFieldName: "createdAt",
          sortMode: "DESC",
          statsOptions: { statsFieldName: "amount", showSum: true },
        }}
      />
    );
  };

  const TradeResource = () => {
    const location = useLocation();
    // Show the ticker symbol instead of the securityId from the trade resource.
    const spec =
      "select ticker from com.ackbox.xpenser.domain.Security inner join @resource on @resource.groupField = id";
    return (
      <PrivateAckResource
        returnTo={location.pathname}
        apiPath={RESOURCES_API_PATH}
        name="trades"
        listOptions={{
          groupFieldLabel: "Security",
          groupFieldName: "securityId",
          transformSpec: spec,
        }}
      />
    );
  };

  const RegularResource = () => {
    const location = useLocation();
    const { name } = useParams();
    return (
      <PrivateAckResource
        returnTo={location.pathname}
        apiPath={RESOURCES_API_PATH}
        name={name!}
      />
    );
  };

  const PrivateOverallSummary = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? <OverallSummary /> : <FrontPage />;
  };

  const PrivateMonthlySummary = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? <MonthlySummary /> : <FrontPage />;
  };

  const PrivateYearlySummary = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? <YearlySummary /> : <FrontPage />;
  };

  const PrivateSimulations = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? <Simulations /> : <FrontPage />;
  };

  const PrivateSecuritiesHoldings = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? <SecuritiesHoldingsSummary /> : <FrontPage />;
  };

  const PrivateSecuritiesAcbByYear = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? (
      <SecuritiesAcbByYearSummary />
    ) : (
      <FrontPage />
    );
  };

  const PrivateSecuritiesAcbBySecurity = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? (
      <SecuritiesAcbBySecuritySummary />
    ) : (
      <FrontPage />
    );
  };

  const PrivateTransactionsImport = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? <TransactionsImport /> : <FrontPage />;
  };

  const PrivateChangelogs = () => {
    const auth = useAuth();
    return auth.isAuthenticated ? <Changelogs /> : <FrontPage />;
  };
  return (
    <Router>
      <AckPage settings={settings}>
        <Routes>
          <Route
            path="resources/expenses/*"
            element={<CountableResource name="expenses" />}
          />
          <Route
            path="resources/incomes/*"
            element={<CountableResource name="incomes" />}
          />
          <Route
            path="resources/transfers/*"
            element={<CountableResource name="transfers" />}
          />
          <Route path="resources/trades/*" element={<TradeResource />} />
          <Route path="settings/*" element={<Settings />} />
          <Route path="resources/:name/*" element={<RegularResource />} />
          <Route
            path="summaries/overall/*"
            element={<PrivateOverallSummary />}
          />
          <Route
            path="summaries/monthly/*"
            element={<PrivateMonthlySummary />}
          />
          <Route path="summaries/yearly/*" element={<PrivateYearlySummary />} />
          <Route path="simulations/*" element={<PrivateSimulations />} />
          <Route
            path="securities/holdings/*"
            element={<PrivateSecuritiesHoldings />}
          />
          <Route
            path="securities/acb/yearly/*"
            element={<PrivateSecuritiesAcbByYear />}
          />
          <Route
            path="securities/acb/securities/*"
            element={<PrivateSecuritiesAcbBySecurity />}
          />
          <Route
            path="transactions/import/*"
            element={<PrivateTransactionsImport />}
          />
          <Route path="changelogs/*" element={<PrivateChangelogs />} />
          <Route path="statusz/*" element={<AckStatusz />} />
          <Route path="/" element={<PrivateOverallSummary />} />
        </Routes>
      </AckPage>
    </Router>
  );
};
